import { reactive } from "vue";
import { isTauri } from "../runtime";

export type ContextMenuItem =
  | { type: "separator" }
  | {
      id: string;
      label: string;
      disabled?: boolean;
      action: () => void | Promise<void>;
    };

/**
 * Reactive state consumed by ZwContextMenuHost on web. Tauri never touches
 * this; it uses native Menu.popup instead.
 */
export const contextMenuState = reactive<{
  open: boolean;
  items: ContextMenuItem[];
  anchor: { top: number; left: number; width: number; height: number };
}>({
  open: false,
  items: [],
  anchor: { top: 0, left: 0, width: 0, height: 0 },
});

/**
 * Show a context menu at the pointer position. On Tauri the native OS menu is
 * used; on web a floating HTML panel renders via ZwContextMenuHost.
 */
export async function openContextMenu(
  event: MouseEvent,
  items: ContextMenuItem[],
): Promise<void> {
  event.preventDefault();

  if (isTauri()) {
    const { Menu, MenuItem, PredefinedMenuItem } =
      await import("@tauri-apps/api/menu");
    const nativeItems = [];
    for (const item of items) {
      if ("type" in item && item.type === "separator") {
        nativeItems.push(await PredefinedMenuItem.new({ item: "Separator" }));
      } else if ("id" in item) {
        nativeItems.push(
          await MenuItem.new({
            id: item.id,
            text: item.label,
            enabled: item.disabled !== true,
            action: () => void item.action(),
          }),
        );
      }
    }
    const menu = await Menu.new({ items: nativeItems });
    await menu.popup();
    return;
  }

  // Web: set reactive state so ZwContextMenuHost renders.
  contextMenuState.anchor = {
    top: event.clientY,
    left: event.clientX,
    width: 0,
    height: 0,
  };
  contextMenuState.items = items;
  contextMenuState.open = true;
}
